import { EChartsOption } from 'echarts';

import { toFixedIfNecessary } from '../../../utils';
import { PowerDetailsSeries } from '../../model';
import { coreServicesLabels } from '../../../../core-services.service';
import { getStyleProp, telemetryStyles } from '../../styles.utils';

export const appBuildPowerDailyGraph = (series: PowerDetailsSeries[], max: number, tz: string): EChartsOption => ({
  title: {
    text: coreServicesLabels.powerState,
    left: 'center',
    top: 12,
    textStyle: {
      color: getStyleProp('--grey-400'),
      fontSize: 16,
      fontFamily: getStyleProp('--font-family-brand'),
      fontWeight: 500,
    },
  },
  legend: {
    show: false,
    selected: {
      P_DC_TOT: true,
      P_EXP: false,
      P_LOAD: false,
      P_B_AGGR: false,
    },
  },
  tooltip: {
    confine: true,
    trigger: 'axis',
    formatter: (elements: any): string => {
      const { data: [valuex] } = elements[0];
      const hour = Math.floor((valuex + (12 * 60 * 60)) / ( 60 * 60));
      const minutes = Math.floor(((valuex + (12 * 60 * 60)) / 60) % 60);
      let template = `
      <div class="fx-row text-accent fx-align-center fx-justify-between mb-2">
        <div class="body-3 mr-3">${coreServicesLabels.time}</div>
        <div class="text-mono">${hour}:${minutes <= 9 ? ('0' + minutes) : minutes} ${hour < 12 ? 'AM' : 'PM'}</div>
      </div>`;
      for (const element of elements) {
        const { seriesName, data: [_, valuey] } = element;
        const isPositive = valuey > 0;
        const { name, color } = telemetryStyles(seriesName, isPositive);
        if (valuey !== 0) {
          template += `
          <div class="fx-row ${color} fx-align-center fx-justify-between mb-1">
            <div class="body-3 mr-3">${name}</div>
            <div class="text-mono">${
              seriesName !== 'BP'
                ? (Math.abs(valuey / 1000).toFixed(2) + ' kW')
                : (Math.abs(valuey).toFixed(2) + ' %')
            }</div>
          </div>`;
        }
      }
      return template;
    },
    backgroundColor: getStyleProp('--grey-100'),
    borderColor: getStyleProp('--grey-100'),
    borderRadius: 4,
    textStyle: {
      color: getStyleProp('--white'),
    },
  },
  grid: {
    show: false,
    top: '40',
    bottom: '40',
    left: '40',
    right: '40',
  },
  xAxis: {
    offset: -90, // in pixel
    max: 12 * 60 * 60,
    min: -12 * 60 * 60,
    maxInterval: 12 * 60 * 60 * 2,
    type: 'value',
    axisLine: {
      onZero: true,
      show: false,
    },
    axisTick: {
      show: false,
    },
    splitLine: {
      show: false,
    },
    axisLabel: {
      color: getStyleProp('--grey-300'),
      fontWeight: 'bold',
      inside: true,
      showMinLabel: false,
      showMaxLabel: false,
      formatter: (value: number) => {
        // -43200 - 43200
        const secondPerHour = 60 * 60;
        const hour = Math.round(value / secondPerHour) + 12;
        const hourWithCustomFormat = hour < 10 ? `0${hour.toFixed()}` : hour.toFixed();
        return hour === 12 ? 'Noon' : hourWithCustomFormat;
      },
      hideOverlap: true,
    },
  },
  yAxis: {
    min: -max,
    max,
    type: 'value',
    axisLine: {
      show: false,
      onZero: false,
    },
    splitLine: {
      show: false,
    },
    axisTick: {
      show: false,
    },
    name: 'kW',
    nameLocation: 'end',
    nameTextStyle: {
      color: getStyleProp('--grey-400'),
      fontWeight: 'bold',
    },
    axisLabel: {
      showMinLabel: false,
      showMaxLabel: false,
      color: getStyleProp('--grey-300'),
      fontWeight: 'bold',
      formatter: (value: number) => toFixedIfNecessary(`${value / 1000}`, 2).toString(),
    },
  },
  series: series.map((s) => ({
    name: s.name,
    type: 'line',
    smooth: true,
    color: s.color,
    symbol: 'none',
    areaStyle: {},
    data: s.values?.map(([time, value]) => ([time, value])),
  })),
});

export const appBuildStateOfChargeGraph = (
  series: PowerDetailsSeries, tz: string,
): EChartsOption => ({
  title: {
    text: coreServicesLabels.batteryPercentage,
    left: 'center',
    top: 20,
    textStyle: {
      color: getStyleProp('--grey-400'),
      fontSize: 16,
      fontFamily: getStyleProp('--font-family-brand'),
      fontWeight: 500,
    },
  },
  grid: {
    show: false,
    top: '40',
    bottom: '10',
    left: '40',
    right: '40',
  },
  tooltip: {
    confine: true,
    trigger: 'axis',
    formatter: (elements: any): string => {
      const { data: [valuex, valueY] } = elements[0];
      const hour = Math.floor((valuex + (12 * 60 * 60)) / ( 60 * 60));
      const minutes = Math.floor(((valuex + (12 * 60 * 60)) / 60) % 60);
      const template = `
      <div class="fx-row text-accent fx-align-center fx-justify-between mb-2">
        <div class="body-3 mr-3">${coreServicesLabels.time}</div>
        <div class="text-mono">${hour}:${minutes <= 9 ? ('0' + minutes) : minutes} ${hour < 12 ? 'AM' : 'PM'}</div>
      </div>
      <div
        class="fx-row fx-align-center fx-justify-between mb-1 text-green">
      <div class="body-3 mr-3">${coreServicesLabels.percentage}</div>
      <div class="text-mono">${toFixedIfNecessary(`${valueY}`, 2).toString() } %</div>
      </div>
      `;
      return template;
    },
    backgroundColor: getStyleProp('--grey-100'),
    borderColor: getStyleProp('--grey-100'),
    borderRadius: 4,
    textStyle: {
      color: getStyleProp('--white'),
    },
  },
  xAxis: {
    max: 12 * 60 * 60,
    min: -12 * 60 * 60,
    maxInterval: 12 * 60 * 60 * 2,
    type: 'value',
    axisLine: {
      onZero: true,
      show: false,
    },
    axisTick: {
      show: false,
    },
    splitLine: {
      show: false,
    },
    axisLabel: {
      color: getStyleProp('--grey-300'),
      fontWeight: 'bold',
      inside: true,
      showMinLabel: false,
      showMaxLabel: false,
      formatter: (value: number) => {
        // -43200 - 43200
        const secondPerHour = 60 * 60;
        const hour = Math.round(value / secondPerHour) + 12;
        const hourWithCustomFormat = hour < 10 ? `0${hour.toFixed()}` : hour.toFixed();
        return hour === 12 ? 'Noon' : hourWithCustomFormat;
      },
      hideOverlap: true,
    },
  },
  yAxis: {
    min: -20,
    max: 110,
    type: 'value',
    axisLine: {
      show: false,
      onZero: false,
    },
    splitLine: {
      show: false,
    },
    axisTick: {
      show: false,
    },
    name: '%',
    nameLocation: 'end',
    nameTextStyle: {
      color: getStyleProp('--grey-400'),
      fontWeight: 'bold',
    },
    axisLabel: {
      showMinLabel: false,
      showMaxLabel: false,
      color: getStyleProp('--grey-300'),
      fontWeight: 'bold',
      formatter: (value: number) => toFixedIfNecessary(`${value}`, 2).toString(),
    },
  },
  series: [
    {
      data: series.values?.map(([time, value]) => ([time, value])),
      type: 'line',
      symbolSize: 0,
      smooth: true,
      color: getStyleProp('--green-400'),
      symbol: 'none',
    },
  ],
});

export const appBuildStateOfSocsGraph = (
  series: PowerDetailsSeries[], tz: string,
): EChartsOption => ({
  title: {
    text: coreServicesLabels.batteryPercentage,
    left: 'center',
    top: 20,
    textStyle: {
      color: getStyleProp('--grey-400'),
      fontSize: 16,
      fontFamily: getStyleProp('--font-family-brand'),
      fontWeight: 500,
    },
  },
  grid: {
    show: false,
    top: '40',
    bottom: '60',
    left: '40',
    right: '40',
  },
  legend: {
    show: false,
    selected: series.reduce((acc:{ [key: string]: boolean } , obj) => {
      acc[obj.name] = true;
      return acc;
    }, {})
  },
  tooltip: {
    confine: true,
    trigger: 'axis',
    formatter: (elements: any): string => {
      const { data: [valuex, valueY] } = elements[0];
      const hour = Math.floor((valuex + (12 * 60 * 60)) / ( 60 * 60));
      const minutes = Math.floor(((valuex + (12 * 60 * 60)) / 60) % 60);
      let template = `
      <div class="fx-row text-accent fx-align-center fx-justify-between mb-2">
        <div class="body-3 mr-3">${coreServicesLabels.time}</div>
        <div class="text-mono">${hour}:${minutes <= 9 ? ('0' + minutes) : minutes} ${hour < 12 ? 'AM' : 'PM'}</div>
      </div>`;
      for (const element of elements) {
        const { seriesName, data: [_, valuey] , color} = element;
        const inverterInformation = seriesName.split('_');
        const batteryNumber = inverterInformation.length > 1 ? 'Bat.'+inverterInformation[1] : ''
        const serial = inverterInformation[0]
        if (valuey !== 0) {
          template += `
          <div style="color: ${color}" class="fx-row fx-align-center fx-justify-between mb-1">
            <div class="body-3 mr-3">${serial +" "+batteryNumber}</div>
            <div class="text-mono">${toFixedIfNecessary(`${valuey}`, 2).toString() } %</div>
          </div>`;
        }
      }

      return template;
    },
    backgroundColor: getStyleProp('--grey-100'),
    borderColor: getStyleProp('--grey-100'),
    borderRadius: 4,
    textStyle: {
      color: getStyleProp('--white'),
    },
  },
  xAxis: {
    max: 12 * 60 * 60,
    min: -12 * 60 * 60,
    maxInterval: 12 * 60 * 60 * 2,
    type: 'value',
    axisLine: {
      onZero: true,
      show: true,
    },
    axisTick: {
      show: false,
    },
    splitLine: {
      show: false,
    },
    axisLabel: {
      color: getStyleProp('--grey-300'),
      fontWeight: 'bold',
      inside: true,
      showMinLabel: false,
      showMaxLabel: false,
      formatter: (value: number) => {
        // -43200 - 43200
        const secondPerHour = 60 * 60;
        const hour = Math.round(value / secondPerHour) + 12;
        const hourWithCustomFormat = hour < 10 ? `0${hour.toFixed()}` : hour.toFixed();
        return hour === 12 ? 'Noon' : hourWithCustomFormat;
      },
      hideOverlap: true,
    },
  },
  yAxis: {
    min: -20,
    max: 110,
    type: 'value',
    axisLine: {
      show: false,
      onZero: false,
    },
    splitLine: {
      show: false,
    },
    axisTick: {
      show: false,
    },
    name: '%',
    nameLocation: 'end',
    nameTextStyle: {
      color: getStyleProp('--grey-400'),
      fontWeight: 'bold',
    },
    axisLabel: {
      showMinLabel: false,
      showMaxLabel: false,
      color: getStyleProp('--grey-300'),
      fontWeight: 'bold',
      formatter: (value: number) => toFixedIfNecessary(`${value}`, 2).toString(),
    },
  },
  dataZoom: [
    {
      type: 'inside',
      yAxisIndex: 0,
      start: 0,
    }
  ],
  series: series.map((s) => ({
    name: s.name,
    color: s.color,
    type: 'line',
    smooth: true,
    symbol: 'none',
    lineStyle: {
      opacity: 1,
    },
    data: s.values?.map(([time, value]) => ([time, value])),
  })),
});

export const portalBuildPowerDailyGraph = (series: PowerDetailsSeries[], max: number, tz: string): EChartsOption => ({
  animation: false,
  legend: {
    show: false,
    selected: {
      P_DC_TOT: true,
      P_EXP: true,
      P_LOAD: true,
      P_B_AGGR: true,
    },
  },
  tooltip: {
    confine: true,
    trigger: 'axis',
    formatter: (elements: any): string => {
      const { data: [valuex] } = elements[0];
      const hour = Math.floor((valuex + (12 * 60 * 60)) / ( 60 * 60));
      const minutes = Math.floor(((valuex + (12 * 60 * 60)) / 60) % 60);
      let template = `
      <div class="fx-row text-accent fx-align-center fx-justify-between mb-2">
        <div class="body-3 mr-3">${coreServicesLabels.time}</div>
        <div class="text-mono">${hour}:${minutes <= 9 ? ('0' + minutes) : minutes} ${hour < 12 ? 'AM' : 'PM'}</div>
      </div>`;
      for (const element of elements) {
        const { seriesName, data: [_, valuey] } = element;
        const isPositive = valuey > 0;
        const { name, color } = telemetryStyles(seriesName , isPositive);
        if (valuey !== 0) {
          template += `
          <div class="fx-row ${color} fx-align-center fx-justify-between mb-1">
            <div class="body-3 mr-3">${name}</div>
            <div class="text-mono">${
              seriesName !== 'BP'
                ? (Math.abs(valuey / 1000).toFixed(2) + ' kW')
                : (Math.abs(valuey).toFixed(2) + ' %')
            }</div>
          </div>`;
        }
      }
      return template;
    },
    backgroundColor: getStyleProp('--grey-100'),
    borderColor: getStyleProp('--grey-100'),
    borderRadius: 4,
    textStyle: {
      color: getStyleProp('--white'),
    },
  },
  grid: {
    show: false,
    top: '0',
    bottom: '40',
    left: '40',
    right: '40',
  },
  xAxis: {
    offset: -90, // in pixel
    max: 12 * 60 * 60,
    min: -12 * 60 * 60,
    maxInterval: 12 * 60 * 60 * 2,
    type: 'value',
    axisLine: {
      onZero: true,
      show: false,
    },
    axisTick: {
      show: false,
    },
    splitLine: {
      show: false,
    },
    axisLabel: {
      color: getStyleProp('--grey-300'),
      fontWeight: 'bold',
      inside: true,
      showMinLabel: false,
      showMaxLabel: false,
      formatter: (value: number) => {
        // -43200 - 43200
        const secondPerHour = 60 * 60;
        const hour = Math.round(value / secondPerHour) + 12;
        const hourWithCustomFormat = hour < 10 ? `0${hour.toFixed()}` : hour.toFixed();
        return hour === 12 ? 'Noon' : hourWithCustomFormat;
      },
      hideOverlap: true,
      verticalAlign: 'bottom',
    },
  },
  yAxis: {
    type: 'value',
    axisLine: {
      show: false,
      onZero: false,
    },
    splitLine: {
      show: false,
    },
    axisTick: {
      show: false,
    },
    name: 'kW',
    nameLocation: 'middle',
    nameTextStyle: {
      color: getStyleProp('--grey-400'),
      fontWeight: 'bold',
    },
    axisLabel: {
      show: false,
    },
  },
  series: series.map((s) => ({
    name: s.name,
    type: 'line',
    smooth: true,
    color: s.color,
    symbol: 'none',
    lineStyle: {
      opacity: 0.2,
    },
    areaStyle: {
      color: s.color,
    },
    data: s.values?.map(([time, value]) => ([time, value])),
  })),
});
